import { modularScale, palette, pxToRem } from "@ottatech/design";
import styled, { css } from "styled-components";

import { UserJobPreferences, Company } from "schema";
import { TagsContainer as DefaultTagsContainer } from "components/Tag";
import { Industry } from "components/Icons/Industry";

const TagsContainer = styled(DefaultTagsContainer)`
  gap: ${pxToRem(8)};
`;

const Wrapper = styled.div`
  display: flex;
  gap: ${pxToRem(10)};

  > svg {
    min-width: ${pxToRem(24)};
    min-height: ${pxToRem(24)};
  }
`;

const JobCardSectorTag = styled.span<{ bolded: boolean }>`
  ${props =>
    css`
      background: ${props.bolded
        ? palette.extended.green.shade100
        : palette.grayscale.shade400};
      border-radius: ${pxToRem(8)};
      font-size: ${modularScale()};
      font-weight: ${props.bolded ? "700" : "normal"};
      padding: ${pxToRem(4)} ${pxToRem(8)};
    `}
`;

interface SectorTagsProps {
  company: Pick<Company.Fragment, "sectorTags">;
  user?: Pick<UserJobPreferences.CurrentUser, "sectorTagPreferences">;
}

export function SectorTags({
  company: { sectorTags },
  user,
}: SectorTagsProps): React.ReactElement | null {
  if (sectorTags.length === 0) {
    return null;
  }

  const userPreferences = user?.sectorTagPreferences ?? [];
  const preferencesSet = new Set(userPreferences.map(pref => pref.sectorTagId));

  const orderedSectorTags = sectorTags
    .slice()
    .sort((a, b) =>
      preferencesSet.has(a.id) && !preferencesSet.has(b.id)
        ? -1
        : !preferencesSet.has(a.id) && preferencesSet.has(b.id)
        ? 1
        : 0
    );
  return (
    <Wrapper>
      <Industry width={24} height={24} />
      <TagsContainer alignLeft data-testid="company-sector-tags">
        {orderedSectorTags.map(({ id, value }) => (
          <JobCardSectorTag
            key={id}
            bolded={preferencesSet.has(id)}
            data-testid="company-sector-tag"
          >
            {value}
          </JobCardSectorTag>
        ))}
      </TagsContainer>
    </Wrapper>
  );
}
